import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View, Card } from '@olxbr/design-system'
import SecurityTipsBlock from '../../organisms/SecurityTipsBlock'
import SecurityTipsModal from '../../organisms/SecurityTipsModal'
import SecretWordBlock from '../../organisms/SecretWordBlock'
import ChangePasswordBlock from '../../organisms/ChangePasswordBlock'
import LoginHistoryControlBlock from '../../organisms/LoginHistoryControlBlock'
import AccountDeleteCard from '../../organisms/AccountDeleteCard'
import Lurker from '../../../util/lurker'
import AccountTakeoutRequestModal from '../AccountTakeoutRequestModal'
import { getAccount } from '../../../api/account'
import { getSecretWord } from '../../../api/secretWord'
import { Badge } from '@olxbr/ds-web'

const lurker = new Lurker('security_central')

const Content = styled(View)`
  display: flex;
  flex-direction: column;
`

const CardWrapper = styled(Card)`
  margin-bottom: 24px;
  padding-top: 32px;
  padding-bottom: 32px;
  padding-left: 32px;
  padding-right: 32px;
`

const SecurityTipsCardWrapper = styled(Card)`
  margin-bottom: 24px;
  padding-top: 32px;
  padding-bottom: 32px;
  padding-left: 32px;
  padding-right: 32px;
  border-radius: 8px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 1px;
    background: linear-gradient(270deg, #f28000 0%, #e22828 36%, #6e0ad6 100%);
    border-radius: inherit;
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: subtract;
    pointer-events: none;
  }
`

const BadgeWrapper = styled(View)`
  position: absolute;
  right: 32px;
  z-index: 2;
`

class SecurityCentralTemplate extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      showAccountTakeoutModal: false,
      email: '',
      secretWord: null,
      accountVerified: false,
    }
  }

  componentDidMount() {
    lurker.pageLoadEvt()
    this.loadSecretWord()
    this.loadAccountData()
  }

  onSecurityLinkClick = () => {
    lurker.viewMessageEvt('security_tips_link')
  }

  onVerifyAccountLinkClick = () => {
    lurker.clickEvt('verify_account_link')
  }

  onChangeSecretWordClick = () => {
    lurker.clickEvt(`change_secret_word_link`)
  }

  onChangePasswordClick = type => {
    lurker.clickEvt(`change_password_${type}`)
    const { onChangePassword } = this.props
    if (onChangePassword && typeof onChangePassword === 'function') {
      onChangePassword()
    }
  }

  onDeleteSessionsSuccess = () => {
    lurker.viewMessageEvt('delete_sessions_success')
    const { onDeleteSessionsSuccess } = this.props
    if (onDeleteSessionsSuccess && typeof onDeleteSessionsSuccess === 'function') {
      onDeleteSessionsSuccess()
    }
  }

  onDeleteSessionsError = () => {
    lurker.viewMessageEvt('delete_sessions_error')
  }

  onDeleteAccountClick = event => {
    if (event) event.preventDefault()
    lurker.clickEvt('delete_account')
    const { onDeleteAccount } = this.props
    if (onDeleteAccount && typeof onDeleteAccount === 'function') {
      onDeleteAccount()
    }
  }

  onDeactivateAccountClick = event => {
    event.preventDefault()
    lurker.clickEvt('inactivate_account')
    const { onDeactivateAccount } = this.props
    if (onDeactivateAccount && typeof onDeactivateAccount === 'function') {
      onDeactivateAccount()
    }
  }

  onAccountTakeoutClick = event => {
    event.preventDefault()
    lurker.clickEvt('account_takeout_modal_open')
    this.setState({ showAccountTakeoutModal: true })
  }

  onAccountTakeoutLinkClose = () => {
    lurker.clickEvt('account_takeout_modal_close')
    this.setState({ showAccountTakeoutModal: false })
  }

  onAccountTakeoutRequestSuccess = () => {
    lurker.clickEvt('account_takeout_success')
    const { onDownloadAccountData } = this.props
    if (onDownloadAccountData && typeof onDownloadAccountData === 'function') {
      onDownloadAccountData(this.state.email)
    }
  }

  onMyAccountClick = () => {
    lurker.clickEvt('my_account_click')
    const { onMyAccount } = this.props
    if (onMyAccount && typeof onMyAccount === 'function') {
      onMyAccount()
    }
  }

  loadAccountData = () => {
    getAccount().then(accountData => {
      this.setState({
        email: accountData?.email,
        accountVerified: accountData?.accountVerified ?? false,
      })
    })
  }

  loadSecretWord = () => {
    getSecretWord().then(({ data }) => {
      if (data && data.secretWord) {
        this.setState({ secretWord: data.secretWord })
      }
    })
  }

  render() {
    const { fragment } = this.props
    return (
      <Content>
        {this.state.secretWord && (
          <CardWrapper>
            <SecretWordBlock
              secretWord={this.state.secretWord}
              email={this.state.email}
              onChangeSecretWordClick={this.onChangeSecretWordClick}
            />
          </CardWrapper>
        )}
        <SecurityTipsCardWrapper>
          <BadgeWrapper>
            <Badge size="small" color="gradient" shape="pill">
              Novo
            </Badge>
          </BadgeWrapper>
          <SecurityTipsModal
            onVerifyAccountLink={this.onVerifyAccountLinkClick}
            accountVerified={this.state.accountVerified}
          />
        </SecurityTipsCardWrapper>
        <CardWrapper>
          <SecurityTipsBlock onSecurityLink={this.onSecurityLinkClick} />
        </CardWrapper>
        <CardWrapper>
          <ChangePasswordBlock onChangePassword={this.onChangePasswordClick} />
        </CardWrapper>
        <CardWrapper>
          <LoginHistoryControlBlock
            onChangePassword={this.onChangePasswordClick}
            onSuccess={this.onDeleteSessionsSuccess}
            onError={this.onDeleteSessionsError}
            lurker={lurker}
          />
        </CardWrapper>
        <AccountDeleteCard
          onDeleteAccount={this.onDeleteAccountClick}
          onDeactivateAccount={this.onDeactivateAccountClick}
          onAccountTakeoutLink={this.onAccountTakeoutClick}
          onMyAccountLink={this.onMyAccountClick}
          focus={fragment === 'takeout-status'}
        />
        <AccountTakeoutRequestModal
          open={this.state.showAccountTakeoutModal}
          onClose={this.onAccountTakeoutLinkClose}
          onSuccess={this.onAccountTakeoutRequestSuccess}
          lurker={lurker}
        />
      </Content>
    )
  }
}

SecurityCentralTemplate.propTypes = {
  onDeleteSessionsSuccess: PropTypes.func.isRequired,
  onChangePassword: PropTypes.func.isRequired,
  onDeleteAccount: PropTypes.func.isRequired,
  onDeactivateAccount: PropTypes.func,
  onDownloadAccountData: PropTypes.func,
  onMyAccount: PropTypes.func,
  fragment: PropTypes.string,
  abTests: PropTypes.string,
}

SecurityCentralTemplate.defaultProps = {
  fragment: '',
}

export default SecurityCentralTemplate
